import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core"
import dayjs from "dayjs"
import { FC, useState } from "react"
import {
  ClientFragment,
  Enum_Feature_Type_Enum,
  User_Insights,
} from "../../generated/graphql"
import { useGetUserInsights } from "../../hooks/useGetUserInsights"
import { getIcon } from "../../utils/getInsightIcon"
import { getComparator, stableSort } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { Order, TIME_PERIOD, UserInsightsTableData } from "../../utils/types"
import ChevronRightIcon from "../atoms/ChevronRightIcon"
import { UserInsightsDialog } from "../organisms/UserInsightsDialog"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"

const useStyles = makeStyles((theme) => ({
  table: {
    color: "var(--vira-ink, #1E263C)",
    borderRadius: 0,
    boxShadow: "none",
    border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    marginTop: theme.spacing(3),
    "& .MuiTableCell-root": {
      color: "var(--vira-ink, #1E263C)",
      borderColor: `var(--vira-border, ${colors.neutral.clay})`,
      backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    },
  },
  tableHeaderCell: {
    color: "var(--vira-ink, #1E263C)",
    fontWeight: 700,
    borderBottom: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
  },
  tableSortLabel: {
    color: "var(--vira-ink, #1E263C) !important",
    fontWeight: 700,
  },
  noData: {
    width: "100%",
    padding: 27,
    textAlign: "center",
    border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    borderTop: 0,
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    color: "var(--vira-muted, #5C6578)",
  },
  noDataFirstLine: {
    fontWeight: 700,
    marginBottom: 6,
    color: "var(--vira-ink, #1E263C)",
  },
  hyperlink: {
    color: "var(--vira-lavender-text, #7591E4)",
    textDecoration: "underline",
    cursor: "pointer",
  },
  row: {
    fontWeight: "bold",
  },
  questionnaireColumn: {
    display: "flex",
    alignItems: "center",
  },
  questionnaireScore: {
    height: 33,
    width: 33,
    borderRadius: 4,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  helpIconContainer: {
    display: "flex",
    alignItems: "center",
  },
  helpIcon: {
    marginLeft: theme.spacing(2),
    height: 24,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--vira-ink, #1E263C)",
  },
  chartContainer: {
    position: "relative",
    backgroundColor: `var(--vira-paper, ${colors.neutral.white})`,
    padding: theme.spacing(3),
    border: `1px solid var(--vira-border, ${colors.neutral.clay})`,
    borderRadius: 4,
  },
}))

const UserInsightsTable: FC<{
  client: ClientFragment
  timePeriod: TIME_PERIOD
}> = ({ client, timePeriod }) => {
  const classes = useStyles()
  const [item, setItem] = useState<UserInsightsTableData | undefined>(undefined)
  const [page] = useState(0)
  const [icon] = useState<string | undefined>(undefined)
  const [feature] = useState<string | undefined>(undefined)

  const [rowsPerPage] = useState(10)
  const [openScheduleDialog, setOpenScheduleDialog] = useState(false)
  const [order] = useState<Order>("asc")
  const [orderBy] = useState<keyof UserInsightsTableData>("date")
  const sevenDaysAgo = dayjs().hour(0).minute(0).second(0).subtract(7, "days")
  const oneMonthAgo = dayjs().hour(0).minute(0).second(0).subtract(30, "days")
  const userInsights = useGetUserInsights(client.id).filter((x) => {
    if (timePeriod === TIME_PERIOD.WEEK) {
      return dayjs(x.date.data).isAfter(sevenDaysAgo)
    } else if (timePeriod === TIME_PERIOD.MONTH) {
      // return dayjs(x.date.data).isAfter(oneMonthAgo)
      return dayjs(x.date.data)
    }
    return x
  })

  const { showMVP2Features } = useOrganizationFeatureFlags()
  const createInsight = (userInsight: UserInsightsTableData, text: string) => {
    let image = getIcon(
      userInsight.insight.data.insight_combo.feature,
      showMVP2Features
    )
    if (!userInsight.insight.data.insight_combo.feature) return
    if (
      userInsight.insight.data.insight_combo.feature ===
      Enum_Feature_Type_Enum.InvalidNotEnoughData
    )
      return null
    const insightText = userInsight.display_text.data
    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "20px",
            }}
          >
            <img
              src={image}
              alt="Icon"
              height={"25px"}
              style={{ marginRight: "5px" }}
            />
            {" " + insightText}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "20px",
            }}
          >
            <ChevronRightIcon />
          </div>
        </div>
        <div
          style={{
            fontWeight: 300,
          }}
        >
          {text}
        </div>
      </>
    )
  }
  return (
    <>
      <Table className={classes.table}>
        {userInsights && userInsights.length > 0 && (
          <TableBody>
            {stableSort(
              rowsPerPage > 0
                ? userInsights.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                : userInsights,
              getComparator(order, orderBy)
            ).map((row, index) => {
              const onClick = () => {
                if (!row.insight.data.insightRecipe) return
                setOpenScheduleDialog(true)
                setItem(row)
              }
              return (
                <TableRow key={index}>
                  <TableCell
                    style={{ margin: "5px" }}
                    className={classes.row}
                    onClick={onClick}
                    role="button"
                  >
                    {createInsight(row, "")}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </Table>
      <UserInsightsDialog
        insightId={item?.insight.data.insightRecipe.id ?? 0}
        open={openScheduleDialog}
        onClose={() => {
          setOpenScheduleDialog(false)
        }}
      />
    </>
  )
}

export default UserInsightsTable
